"use client";

import React, {
  createContext,
  useReducer,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import { generateAISummaryCacheKey } from "@/lib/utils/ai/cache-key-generator";
import { summaryReducer, initialState } from "./reducer";
import type { AISummaryContextValue, SummaryData, SummaryRequest } from "./types";

export const AISummaryContext = createContext<AISummaryContextValue | undefined>(undefined);

async function getAuthToken(): Promise<string | null> {
  try {
    const supabase = getBrowserSupabaseClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (error) {
    console.error("Failed to get auth token:", error);
    return null;
  }
}

export function AISummaryProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(summaryReducer, initialState);
  const inFlightRef = useRef<Map<string, Promise<string>>>(new Map());

  const generateSummary = useCallback(
    async (request: SummaryRequest): Promise<string> => {
      const cacheKey = generateAISummaryCacheKey(
        request.entityType || (request.contactId ? "contact" : "unknown"),
        request.entityId || request.contactId || "unknown",
        request.summaryType,
        request.options
      );

      if (!request.forceRefresh && state.summaries[cacheKey]) {
        return state.summaries[cacheKey].content;
      }

      const existing = inFlightRef.current.get(cacheKey);
      if (existing) {
        return existing;
      }

      dispatch({ type: "START_LOADING", cacheKey });

      const runRequest = async (): Promise<string> => {
        try {
          const token = await getAuthToken();
          if (!token) {
            throw new Error("No authentication token available");
          }

          const response = await fetch("/api/ai/summary", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              entityType: request.entityType || (request.contactId ? "contact" : undefined),
              entityId: request.entityId || request.contactId,
              contactId: request.contactId,
              summaryType: request.summaryType,
              options: request.options,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to generate summary");
          }

          const contentType = response.headers.get("content-type");
          const isStreaming =
            contentType?.includes("text/plain") ||
            response.headers.get("transfer-encoding") === "chunked";

          if (isStreaming) {
            dispatch({ type: "START_STREAMING", cacheKey });

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let content = "";

            if (reader) {
              try {
                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;

                  const chunk = decoder.decode(value, { stream: true });
                  content += chunk;

                  dispatch({ type: "UPDATE_CONTENT", cacheKey, content });
                }
              } finally {
                reader.releaseLock();
              }
            }

            const cacheStatus = response.headers.get("X-Cache-Status");
            const timelineCached = response.headers.get("X-Timeline-Cached") === "true";
            const sanitizedCached = response.headers.get("X-Sanitized-Cached") === "true";
            const summaryData: SummaryData = {
              content,
              cacheKey,
              contactId: request.contactId || request.entityId || "",
              summaryType: request.summaryType,
              createdAt: new Date(),
              metadata: {
                contactInfo: {},
                activityCount: parseInt(response.headers.get("X-Activity-Count") || "0"),
                processingTime: 0,
                cacheHit: cacheStatus === "hit",
                timelineCacheHit: timelineCached,
                sanitizedCacheHit: sanitizedCached,
              },
            };

            dispatch({ type: "SET_SUMMARY", cacheKey, summary: summaryData });
            return content;
          } else {
            const data = await response.json();

            const summaryData: SummaryData = {
              content: data.summary,
              cacheKey: data.cacheKey || cacheKey,
              contactId: request.contactId || request.entityId || "",
              summaryType: request.summaryType,
              createdAt: new Date(),
              metadata: {
                ...data.metadata,
                cacheHit: data.cacheHit,
                timelineCacheHit: data.timelineCacheHit,
                sanitizedCacheHit: data.sanitizedCacheHit,
              },
            };

            dispatch({ type: "SET_SUMMARY", cacheKey, summary: summaryData });
            return data.summary;
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
          dispatch({ type: "SET_ERROR", cacheKey, error: errorMessage });
          throw error;
        }
      };

      const promise = runRequest();
      inFlightRef.current.set(cacheKey, promise);
      return promise.finally(() => inFlightRef.current.delete(cacheKey));
    },
    [state.summaries]
  );

  const getSummary = useCallback(
    (cacheKey: string): SummaryData | undefined => {
      return state.summaries[cacheKey];
    },
    [state.summaries]
  );

  const clearSummary = useCallback((cacheKey: string) => {
    dispatch({ type: "CLEAR_SUMMARY", cacheKey });
  }, []);

  const clearAllSummaries = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  const refreshSummary = useCallback(
    async (request: SummaryRequest): Promise<string> => {
      return generateSummary({ ...request, forceRefresh: true });
    },
    [generateSummary]
  );

  const isLoading = useCallback(
    (cacheKey: string): boolean => {
      return !!state.loadingStates[cacheKey];
    },
    [state.loadingStates]
  );

  const isStreaming = useCallback(
    (cacheKey: string): boolean => {
      return !!state.streamingStates[cacheKey];
    },
    [state.streamingStates]
  );

  const hasError = useCallback(
    (cacheKey: string): boolean => {
      return !!state.errors[cacheKey];
    },
    [state.errors]
  );

  const getError = useCallback(
    (cacheKey: string): string | undefined => {
      return state.errors[cacheKey] || undefined;
    },
    [state.errors]
  );

  const contextValue: AISummaryContextValue = useMemo(
    () => ({
      summaries: state.summaries,
      loadingStates: state.loadingStates,
      errors: state.errors,
      streamingStates: state.streamingStates,

      generateSummary,
      getSummary,
      clearSummary,
      clearAllSummaries,
      refreshSummary,

      generateCacheKey: (request: SummaryRequest) =>
        generateAISummaryCacheKey(
          request.entityType || (request.contactId ? "contact" : "unknown"),
          request.entityId || request.contactId || "unknown",
          request.summaryType,
          request.options
        ),
      isLoading,
      isStreaming,
      hasError,
      getError,
    }),
    [
      state.summaries,
      state.loadingStates,
      state.errors,
      state.streamingStates,
      generateSummary,
      getSummary,
      clearSummary,
      clearAllSummaries,
      refreshSummary,
      isLoading,
      isStreaming,
      hasError,
      getError,
    ]
  );

  return <AISummaryContext.Provider value={contextValue}>{children}</AISummaryContext.Provider>;
}
